/**
 * Builds the 118-element periodic table the interactive widget draws: group, period,
 * block, electron configuration, grid position and the colour classes. Sources and
 * conventions are in `periodic-data.ts`.
 */

import {
  CATEGORIES,
  type CategoryId,
  CONFIG_EXCEPTIONS,
  DISCOVERY_YEARS,
  ELECTRONEGATIVITY,
  FIRST_PREDICTED_CONFIG,
} from "@/lib/tool-kit/chem-reference/periodic-data";
import { type ChemElement, ELEMENTS } from "@/lib/tool-kit/chemistry/elements";

export type BlockId = "s" | "p" | "d" | "f";
export type SubshellLetter = "s" | "p" | "d" | "f";

export interface Subshell {
  n: number;
  l: SubshellLetter;
  count: number;
}

export interface Configuration {
  /** Symbol of the noble-gas core, or null when the configuration starts at 1s. */
  core: string | null;
  /** Subshells outside the core, ordered by n and then ℓ. */
  shells: Subshell[];
  /** True when no measurement exists and the configuration is a prediction. */
  predicted: boolean;
}

export interface PeriodicElement {
  z: number;
  symbol: string;
  name: string;
  weight: number;
  /** True when `weight` is a mass number in brackets, not a standard atomic weight. */
  massNumber: boolean;
  /** 1–18; null for the f-block elements La–Yb and Ac–No. */
  group: number | null;
  period: number;
  block: BlockId;
  category: CategoryId;
  electronegativity: number | null;
  /** Year of discovery; null for elements known since antiquity. */
  discovered: number | null;
  /** Grid row and column (1-based) in the drawn table. */
  row: number;
  col: number;
}

const NOBLE_CORES: readonly (readonly [number, string])[] = [
  [86, "Rn"],
  [54, "Xe"],
  [36, "Kr"],
  [18, "Ar"],
  [10, "Ne"],
  [2, "He"],
];

/** Subshells in the Madelung (n + ℓ, then n) filling order up to 7p. */
const FILL_ORDER: readonly (readonly [number, SubshellLetter])[] = [
  [1, "s"],
  [2, "s"],
  [2, "p"],
  [3, "s"],
  [3, "p"],
  [4, "s"],
  [3, "d"],
  [4, "p"],
  [5, "s"],
  [4, "d"],
  [5, "p"],
  [6, "s"],
  [4, "f"],
  [5, "d"],
  [6, "p"],
  [7, "s"],
  [5, "f"],
  [6, "d"],
  [7, "p"],
];

const CAPACITY: Record<SubshellLetter, number> = { s: 2, p: 6, d: 10, f: 14 };
const SUBSHELL_RANK: Record<SubshellLetter, number> = {
  s: 0,
  p: 1,
  d: 2,
  f: 3,
};
const SUBSHELL_TOKEN = /(\d)([spdf])(\d+)/g;

function bySubshell(a: Subshell, b: Subshell): number {
  return a.n - b.n || SUBSHELL_RANK[a.l] - SUBSHELL_RANK[b.l];
}

/** Madelung filling of `electrons` electrons as subshell occupancies. */
function madelung(electrons: number): Subshell[] {
  const shells: Subshell[] = [];
  let left = electrons;
  for (const [n, l] of FILL_ORDER) {
    if (left <= 0) {
      break;
    }
    const count = Math.min(left, CAPACITY[l]);
    shells.push({ n, l, count });
    left -= count;
  }
  return shells;
}

/** Parse "4f14 5d10 6s1" into subshells. */
function parseShells(text: string): Subshell[] {
  return [...text.matchAll(SUBSHELL_TOKEN)].map((match) => ({
    n: Number(match[1]),
    l: match[2] as SubshellLetter,
    count: Number(match[3]),
  }));
}

function coreFor(z: number): { z: number; symbol: string } | null {
  for (const [coreZ, symbol] of NOBLE_CORES) {
    if (coreZ < z) {
      return { z: coreZ, symbol };
    }
  }
  return null;
}

/** Ground-state electron configuration of the neutral atom with atomic number `z`. */
export function configuration(z: number): Configuration {
  const core = coreFor(z);
  const predicted = z >= FIRST_PREDICTED_CONFIG;
  const exception = CONFIG_EXCEPTIONS[z];
  if (exception) {
    return {
      core: core?.symbol ?? null,
      shells: parseShells(exception).sort(bySubshell),
      predicted,
    };
  }
  const coreShells = core ? madelung(core.z) : [];
  const shells = madelung(z)
    .map((shell) => {
      const inCore =
        coreShells.find((c) => c.n === shell.n && c.l === shell.l)?.count ?? 0;
      return { ...shell, count: shell.count - inCore };
    })
    .filter((shell) => shell.count > 0)
    .sort(bySubshell);
  return { core: core?.symbol ?? null, shells, predicted };
}

/** "[Ar] 3d6 4s2" in plain text, subshell counts as digits. */
export function configurationText(config: Configuration): string {
  const shells = config.shells.map((s) => `${s.n}${s.l}${s.count}`).join(" ");
  return config.core ? `[${config.core}] ${shells}` : shells;
}

/** The full configuration with the core written out, ordered by n and then ℓ. */
export function expandedConfiguration(z: number): Subshell[] {
  const core = coreFor(z);
  const config = configuration(z);
  const merged = new Map<string, Subshell>();
  for (const shell of [...(core ? madelung(core.z) : []), ...config.shells]) {
    merged.set(`${shell.n}${shell.l}`, shell);
  }
  return [...merged.values()].sort(bySubshell);
}

/** Period (row) of an element: 1–7. */
export function periodOf(z: number): number {
  if (z <= 2) {
    return 1;
  }
  if (z <= 10) {
    return 2;
  }
  if (z <= 18) {
    return 3;
  }
  if (z <= 36) {
    return 4;
  }
  if (z <= 54) {
    return 5;
  }
  return z <= 86 ? 6 : 7;
}

const LANTHANIDE_START = 57;
const ACTINIDE_START = 89;
const F_BLOCK_WIDTH = 14;
const F_FIRST_COLUMN = 3;
const LANTHANIDE_ROW = 9;
const ACTINIDE_ROW = 10;

function isFBlock(z: number): boolean {
  return (
    (z >= LANTHANIDE_START && z < LANTHANIDE_START + F_BLOCK_WIDTH) ||
    (z >= ACTINIDE_START && z < ACTINIDE_START + F_BLOCK_WIDTH)
  );
}

/** IUPAC group number 1–18, or null for the f-block rows. */
export function groupOf(z: number): number | null {
  if (z === 1) {
    return 1;
  }
  if (z === 2) {
    return 18;
  }
  if (isFBlock(z)) {
    return null;
  }
  const period = periodOf(z);
  const starts = [0, 2, 10, 18, 36, 54, 86];
  const offset = z - starts[period - 1] - 1;
  if (period <= 3) {
    return offset < 2 ? offset + 1 : offset + 11;
  }
  if (period <= 5) {
    return offset + 1;
  }
  if (offset < 2) {
    return offset + 1;
  }
  // Two s-block elements, then the 14 f-block elements, then lutetium/lawrencium in group 3.
  return offset - (2 + F_BLOCK_WIDTH) + 3;
}

function blockOf(z: number, group: number | null): BlockId {
  if (group === null) {
    return "f";
  }
  if (group <= 2 || z === 2) {
    return "s";
  }
  return group <= 12 ? "d" : "p";
}

function position(z: number, group: number | null, period: number) {
  if (group !== null) {
    return { row: period, col: group };
  }
  const base = z >= ACTINIDE_START ? ACTINIDE_START : LANTHANIDE_START;
  return {
    row: z >= ACTINIDE_START ? ACTINIDE_ROW : LANTHANIDE_ROW,
    col: F_FIRST_COLUMN + (z - base),
  };
}

function build(element: ChemElement): PeriodicElement {
  const { z } = element;
  const group = groupOf(z);
  const period = periodOf(z);
  const year = DISCOVERY_YEARS[z - 1];
  return {
    z,
    symbol: element.symbol,
    name: element.name,
    weight: element.weight,
    massNumber: element.massNumber,
    group,
    period,
    block: blockOf(z, group),
    category: CATEGORIES[z - 1],
    electronegativity: ELECTRONEGATIVITY[z - 1],
    discovered: year === 0 ? null : year,
    ...position(z, group, period),
  };
}

/** All 118 elements in atomic-number order. */
export const PERIODIC_ELEMENTS: readonly PeriodicElement[] =
  ELEMENTS.map(build);

export function elementByZ(z: number): PeriodicElement | undefined {
  return PERIODIC_ELEMENTS[z - 1];
}

/* ------------------------------------------------------------ colour modes */

export type ColorMode =
  | "category"
  | "block"
  | "electronegativity"
  | "discovery";

export const COLOR_MODES: readonly ColorMode[] = [
  "category",
  "block",
  "electronegativity",
  "discovery",
];

export interface ColorClass {
  id: string;
  /** Short marker printed in the cell next to the symbol. */
  code: string;
}

const CATEGORY_CLASSES: readonly ColorClass[] = [
  { id: "alkali", code: "AM" },
  { id: "alkaline", code: "AE" },
  { id: "transition", code: "TM" },
  { id: "post", code: "PT" },
  { id: "metalloid", code: "MD" },
  { id: "nonmetal", code: "NM" },
  { id: "halogen", code: "HL" },
  { id: "noble", code: "NG" },
  { id: "lanthanide", code: "LN" },
  { id: "actinide", code: "AC" },
];

const BLOCK_CLASSES: readonly ColorClass[] = [
  { id: "blockS", code: "s" },
  { id: "blockP", code: "p" },
  { id: "blockD", code: "d" },
  { id: "blockF", code: "f" },
];

/** Lower bounds of the electronegativity bands, in order. */
const EN_BANDS = [0, 1, 1.5, 2, 2.5, 3] as const;

const EN_CLASSES: readonly ColorClass[] = [
  { id: "en1", code: "1" },
  { id: "en2", code: "2" },
  { id: "en3", code: "3" },
  { id: "en4", code: "4" },
  { id: "en5", code: "5" },
  { id: "en6", code: "6" },
  { id: "enNone", code: "–" },
];

const ERA_CLASSES: readonly ColorClass[] = [
  { id: "ancient", code: "A" },
  { id: "before1800", code: "B" },
  { id: "y1800s", code: "C" },
  { id: "y1900a", code: "D" },
  { id: "y1950", code: "E" },
];

export const CLASSES_BY_MODE: Record<ColorMode, readonly ColorClass[]> = {
  category: CATEGORY_CLASSES,
  block: BLOCK_CLASSES,
  electronegativity: EN_CLASSES,
  discovery: ERA_CLASSES,
};

const ERA_1800 = 1800;
const ERA_1900 = 1900;
const ERA_1950 = 1950;

function eraClass(year: number | null): string {
  if (year === null) {
    return "ancient";
  }
  if (year < ERA_1800) {
    return "before1800";
  }
  if (year < ERA_1900) {
    return "y1800s";
  }
  return year < ERA_1950 ? "y1900a" : "y1950";
}

function electronegativityClass(value: number | null): string {
  if (value === null) {
    return "enNone";
  }
  let band = 0;
  for (const [index, bound] of EN_BANDS.entries()) {
    if (value >= bound) {
      band = index;
    }
  }
  return `en${band + 1}`;
}

/** The id of the colour class an element falls in under a mode. */
export function classOf(element: PeriodicElement, mode: ColorMode): string {
  if (mode === "category") {
    return element.category;
  }
  if (mode === "block") {
    return `block${element.block.toUpperCase()}`;
  }
  if (mode === "electronegativity") {
    return electronegativityClass(element.electronegativity);
  }
  return eraClass(element.discovered);
}

/** How many elements fall in each class of a mode. */
export function classCounts(mode: ColorMode): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const cls of CLASSES_BY_MODE[mode]) {
    counts[cls.id] = 0;
  }
  for (const element of PERIODIC_ELEMENTS) {
    const id = classOf(element, mode);
    counts[id] += 1;
  }
  return counts;
}

/* ------------------------------------------------------------------ search */

/**
 * Elements matching a search: a name fragment, a symbol, an atomic number, or a category
 * name supplied by the caller (so translated labels work). Empty query matches all.
 */
export function matchElement(
  element: PeriodicElement,
  query: string,
  categoryLabel: string
): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") {
    return true;
  }
  return (
    element.name.toLowerCase().includes(q) ||
    element.symbol.toLowerCase() === q ||
    String(element.z) === q ||
    categoryLabel.toLowerCase().includes(q)
  );
}

/* --------------------------------------------------------- keyboard moves */

export type NavKey =
  | "ArrowLeft"
  | "ArrowRight"
  | "ArrowUp"
  | "ArrowDown"
  | "Home"
  | "End";

const CELLS_BY_ROW = (() => {
  const rows = new Map<number, PeriodicElement[]>();
  for (const element of PERIODIC_ELEMENTS) {
    const list = rows.get(element.row) ?? [];
    list.push(element);
    rows.set(element.row, list);
  }
  for (const list of rows.values()) {
    list.sort((a, b) => a.col - b.col);
  }
  return rows;
})();

const ROW_NUMBERS = [...CELLS_BY_ROW.keys()].sort((a, b) => a - b);

function nearestInRow(row: number, col: number): PeriodicElement {
  const cells = CELLS_BY_ROW.get(row) ?? [];
  let best = cells[0];
  for (const cell of cells) {
    if (Math.abs(cell.col - col) < Math.abs(best.col - col)) {
      best = cell;
    }
  }
  return best;
}

/** The element a roving-focus key moves to from `z`; `z` itself when it cannot move. */
export function moveFocus(z: number, key: NavKey): number {
  const from = PERIODIC_ELEMENTS[z - 1];
  const rowCells = CELLS_BY_ROW.get(from.row) ?? [];
  const index = rowCells.findIndex((cell) => cell.z === z);
  if (key === "ArrowLeft") {
    return rowCells[Math.max(0, index - 1)].z;
  }
  if (key === "ArrowRight") {
    return rowCells[Math.min(rowCells.length - 1, index + 1)].z;
  }
  if (key === "Home") {
    return rowCells[0].z;
  }
  if (key === "End") {
    return rowCells.at(-1)?.z ?? z;
  }
  const rowIndex = ROW_NUMBERS.indexOf(from.row);
  const target = ROW_NUMBERS[rowIndex + (key === "ArrowUp" ? -1 : 1)];
  return target === undefined ? z : nearestInRow(target, from.col).z;
}
